import { T, useTranslate } from '@tolgee/react';
import { Alert } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { CheckSvg, CrossSvg } from '../../../assets/icons';
import DisabledSvg from '../../../assets/icons/DisabledSvg';
import { MagicWandSvg } from '../../../assets/icons/MagicWandSvg';
import UndoSvg from '../../../assets/icons/UndoSvg';
import theme from '../../../assets/theme';
import { AiAssessedScore } from '../../../frontendServices/gameAPI';
import useAiAssessedScores from '../../../hooks/useAiAssessedScores';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useImagePreview from '../../../hooks/useImagePreview';
import { useMetrics } from '../../../hooks/useMetrics';
import useMixpanelFlag from '../../../hooks/useMixpanelFlag';
import useOpenAiStatus from '../../../hooks/useOpenAiStatus';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { ActivateCurimoteContainer } from '../../../pages/[tenantId]/games/[gameId]/curimote';
import {
  ById,
  PartialParticipant,
  Response,
} from '../../../services/backendService/types';
import { RoundState } from '../../../utils/roundUtils';
import ActivationOverlay from '../../_molecules/ActivityOverlay';
import { SlideType } from '../../SlideComponents/slide.types';
import AiAssessedAnswersListItem from '../AiAssessedAnswersListItem';
import { Button } from '../Button';
import { DrawingImageGridItem } from '../DismissableQuestionList/DismissableQuestionList';
import { IconWrapper } from '../IconWrapper';

const ListItem = ({
  response,
  participantsById,
  participantIsFinishedStatus,
  onDismiss,
  slideType,
  requireRealNames,
  aiAssessedScore,
  isFetchingAiAssessedScores,
  gameId,
  slideId,
}: {
  slideType: SlideType;
  response: Response;
  participantIsFinishedStatus: 'writing' | 'done' | 'notStarted' | 'absent';
  requireRealNames?: boolean;
  participantsById: ById<PartialParticipant>;
  onDismiss: (data: { responseId: string; dismiss: boolean }) => void;
  aiAssessedScore?: AiAssessedScore;
  isFetchingAiAssessedScores: boolean;
  gameId: string;
  slideId: string;
}) => {
  const { t } = useTranslate();
  const isLargerThan650 = useBreakPoint(650);
  const isDismissed = response.dismissed;
  const participant = participantsById[response.uid];

  const splitResponse = useMemo(() => response.response.split('\n'), [response.response]);

  const { imagePreviewComponent, onImageClicked } = useImagePreview('image');

  return (
    <ResponseItemContainer
      $dismissed={isDismissed}
      $isAiAssessedAnswers={
        slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
      }
    >
      {imagePreviewComponent}
      <ResponseWrapper
        $isAiAssessedAnswers={
          slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
        }
        style={{
          gridTemplateColumns:
            (slideType === 'aiAssessedAnswers' ||
              slideType === 'aiAssessedImageAnswers') &&
            !!aiAssessedScore &&
            !isFetchingAiAssessedScores &&
            isLargerThan650
              ? '1fr 1fr'
              : '1fr',
        }}
      >
        <ResponseTextContainer
          $dismissed={isDismissed}
          $isAiAssessedAnswers={
            slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
          }
          $aiAssessedScore={aiAssessedScore}
        >
          <HorizontalContainer>
            <PlaceMobile>
              <span>
                {/* Show real name */}
                {participant &&
                  requireRealNames &&
                  (participant.name || participant.nickName)}
                {/* Show nick name */}
                {participant && !requireRealNames && participant.nickName}
              </span>
            </PlaceMobile>
            {!isDismissed && slideType !== 'poll' && (
              <Button
                onClick={() => onDismiss({ responseId: response.id, dismiss: true })}
                icon={<DisabledSvg />}
                height="32px"
                styles={{ color: '#c44137', borderColor: '#c44137', padding: '0px 5px' }}
                type="tertiary"
              >
                {t('Dismiss')}
              </Button>
            )}
            {isDismissed && (
              <Button
                icon={<UndoSvg />}
                height="32px"
                type="tertiary"
                styles={{ color: theme.colors.black, borderColor: theme.colors.black }}
                onClick={() => onDismiss({ responseId: response.id, dismiss: false })}
              >
                {t('Undo')}
              </Button>
            )}
          </HorizontalContainer>

          <>
            {slideType === 'aiAssessedImageAnswers' && (
              <QuestionMobile $borderColor={'transparent'}>
                <DrawingImageGridItem
                  isAuthenticated={true}
                  response={response}
                  onClick={onImageClicked}
                />
              </QuestionMobile>
            )}
            {slideType !== 'aiAssessedImageAnswers' && (
              <QuestionMobile>
                <div
                  style={{
                    fontSize: response.response.length <= 300 ? '18px' : '16px',
                  }}
                >
                  {splitResponse.map((r, i) => (
                    <p key={i}>{r}</p>
                  ))}
                </div>
                <p
                  style={{
                    textAlign: 'right',
                    width: '100%',
                    marginBottom: 0,
                    color:
                      participantIsFinishedStatus === 'done'
                        ? theme.colors.green
                        : theme.colors.black,
                  }}
                >
                  {participantIsFinishedStatus === 'writing' ? (
                    <>
                      <T keyName="Writing" defaultValue="Writing" />{' '}
                      <LoadingDots>...</LoadingDots>
                    </>
                  ) : participantIsFinishedStatus === 'notStarted' ? (
                    <T keyName="Not yet started" defaultValue="Not yet started" />
                  ) : (
                    <>
                      <CheckSvg /> <T keyName="Done" defaultValue="Done" />
                    </>
                  )}
                </p>
              </QuestionMobile>
            )}
          </>
        </ResponseTextContainer>
        {(slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers') &&
          aiAssessedScore !== undefined && (
            <AiAssessedAnswersListItem
              response={response}
              aiAssessedScore={aiAssessedScore}
              isFetchingAiAssessedScores={isFetchingAiAssessedScores}
              gameId={gameId}
              slideId={slideId}
              participantName={participant && (participant.name || participant.nickName)}
            />
          )}
      </ResponseWrapper>
    </ResponseItemContainer>
  );
};

const HorizontalContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
`;

const ResponseItemContainer = styled.li<{
  $dismissed?: boolean;
  $isAiAssessedAnswers: boolean;
}>`
  width: 100%;
  display: flex;
  background-color: ${theme.colors.white};
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  flex-direction: column;
  height: fit-content;
  padding: 10px 20px 20px 20px;
  & > * {
    margin-top: 5px;
    margin-bottom: 5px;
  }
  ${({ $dismissed }) =>
    $dismissed &&
    css`
      background-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
      opacity: 0.5;
    `}
  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      background-color: ${theme.colors.background};
      box-shadow: none;
      padding: 0;
      height: 100%;

      @media (max-width: 900px) {
        height: fit-content;
      }
    `}
`;
const ResponseTextContainer = styled.div<{
  $dismissed?: boolean;
  $isAiAssessedAnswers: boolean;
  $aiAssessedScore: AiAssessedScore | undefined;
}>`
  width: 100%;
  height: fit-content;
  ${({ $dismissed, $isAiAssessedAnswers }) =>
    $dismissed &&
    $isAiAssessedAnswers &&
    css`
      background-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
    `}
  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      width: 100%;
      padding: 20px;
      padding-bottom: 5px;
      background-color: ${theme.colors.white};
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
      border-radius: 20px;
      margin-bottom: 10px;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      background-color: ${theme.colors.white};
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
    `}
`;

const ResponseWrapper = styled.div<{
  $isAiAssessedAnswers: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: fit-content;

  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      gap: 10px;
      display: grid;
      margin: 0;
      height: 100%;
    `}
`;

const QuestionMobile = styled.div<{ $borderColor?: string }>`
  span {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 24px;
  }
`;
const PlaceMobile = styled.div`
  span {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 36px;
  }

  @media (max-width: 1000px) {
    span {
      font-size: 24px;
    }
  }
`;

export const LoadingDots = styled.p`
  font-weight: bold;
  margin: 0;
  display: inline-block;
  clip-path: inset(0 1.2ch 0 0);
  animation: l 1s steps(4) infinite;

  @keyframes l {
    to {
      clip-path: inset(0 -0.3ch 0 0);
    }
  }
`;

const ParticipantsList = ({
  participants,
  currentSelectedStudentId,
  setCurrentSelectedStudentId,
  findParticipantResponse,
  responseCharacterLimit,
  slideType,
  findParticipantIsFinishedStatus,
}: {
  participants: PartialParticipant[];
  currentSelectedStudentId: string | undefined;
  setCurrentSelectedStudentId: (currentSelectedStudentId: string) => void;
  findParticipantResponse: (responseUid: string | undefined) => Response | undefined;
  responseCharacterLimit: number | null | undefined;
  slideType: SlideType;
  findParticipantIsFinishedStatus: (
    participantId: string,
  ) => 'writing' | 'done' | 'notStarted';
}) => {
  const handleArrowKeyPress = (direction: 'up' | 'down') => {
    if (!currentSelectedStudentId) return;
    const currentIndex = participants.findIndex(p => p.id === currentSelectedStudentId);
    if (direction === 'up') {
      if (currentIndex === 0) {
        setCurrentSelectedStudentId(participants[participants.length - 1].id);
      } else {
        setCurrentSelectedStudentId(participants[currentIndex - 1].id);
      }
    } else {
      if (currentIndex === participants.length - 1) {
        setCurrentSelectedStudentId(participants[0].id);
      } else {
        setCurrentSelectedStudentId(participants[currentIndex + 1].id);
      }
    }
  };

  return (
    <ParticipantsColumn>
      <Hotkeys keyName={'up'} onKeyUp={() => handleArrowKeyPress('up')} />
      <Hotkeys keyName={'down'} onKeyUp={() => handleArrowKeyPress('down')} />
      {participants.map(p => (
        <ParticipantButton
          $status={findParticipantIsFinishedStatus(p.id)}
          $isSelected={currentSelectedStudentId === p.id}
          key={p.id}
          onClick={() => setCurrentSelectedStudentId(p.id)}
        >
          <ParticipantName>{p.name ? p.name : p.nickName}</ParticipantName>
          {findParticipantIsFinishedStatus(p.id) === 'writing' && (
            <div
              style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <p>
                <T keyName="Writing" defaultValue="Writing" />{' '}
                <LoadingDots>...</LoadingDots>
              </p>
              <span style={{ fontSize: 14, color: theme.colors.iconGrey }}>
                {slideType === 'answers' &&
                  `${findParticipantResponse(p.id)?.response.length} characters`}

                {slideType === 'aiAssessedAnswers' &&
                  `${findParticipantResponse(p.id)?.response.length} /
                ${responseCharacterLimit || 1000}`}
              </span>
            </div>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'notStarted' && (
            <p>
              <T keyName="Not yet started" defaultValue="Not yet started" />
            </p>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'done' && (
            <div
              style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <p
                style={{
                  color: theme.colors.green,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CheckSvg /> <T keyName="Done" defaultValue="Done" />
              </p>
              <span style={{ fontSize: 14, color: theme.colors.iconGrey }}>
                {slideType === 'answers' &&
                  `${findParticipantResponse(p.id)?.response.length} characters`}
                {slideType === 'aiAssessedAnswers' &&
                  `${findParticipantResponse(p.id)?.response.length} /
            ${responseCharacterLimit || 1000}`}
              </span>
            </div>
          )}
        </ParticipantButton>
      ))}
    </ParticipantsColumn>
  );
};

const StreamingLongAnswerList = ({
  responses,
  participantsById,
  onDismiss,
  slideType,
  requireRealNames,
  gameId,
  slideId,
  roundState,
  responseCharacterLimit,
}: {
  slideType: SlideType;
  responses: Response[];
  requireRealNames?: boolean;
  participantsById: ById<PartialParticipant>;
  gameId: string;
  slideId: string;
  responseCharacterLimit: number | null | undefined;
  onDismiss: (data: { responseId: string; dismiss: boolean }) => void;
  roundState?: RoundState;
}) => {
  const { tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const {
    generateAllAiAssessedScores,
    aiAssessedScores,
    isFetchingAiAssessedScores,
    error,
  } = useAiAssessedScores({ gameId, slideId, tenantId });
  const isLargerThan800 = useBreakPoint(800);
  const isLargerThan900 = useBreakPoint(900);
  const isLargerThan1000 = useBreakPoint(1000);
  const useOpenAIIsDown = useMixpanelFlag('useOpenAIIsDown'); //TODO: remove this one if the useOpenAiStatus could be used automatic
  const [showGenerateFeedbackOverlay, setShowGenerateFeedbackOverlay] = useState(false);
  const isOpenAiDown = useOpenAiStatus();
  const onFetchAllAiScores = () => {
    generateAllAiAssessedScores();
    metrics.logEvent('AiAssessedAnswers.FetchAllAiAssessedScores.Clicked', {
      gameId,
      roundId: slideId,
    });
  };

  const sortedParticipants = Object.values(participantsById).sort((a, b) => {
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    } else {
      return a.nickName.localeCompare(b.nickName);
    }
  });
  const [currentSelectedStudentId, setCurrentSelectedStudentId] = useState(
    sortedParticipants[0]?.id ? sortedParticipants[0].id : undefined,
  );

  const findParticipantResponse = (responseUid: string | undefined) => {
    const response = responses.find(r => r.uid === responseUid);
    return response;
  };

  useEffect(() => {
    if (!currentSelectedStudentId && sortedParticipants[0]?.id) {
      setCurrentSelectedStudentId(sortedParticipants[0]?.id);
    }
  }, [currentSelectedStudentId, sortedParticipants]);

  const currentStudentResponse = useMemo(() => {
    if (!currentSelectedStudentId) return undefined;
    return findParticipantResponse(currentSelectedStudentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedStudentId, responses]);

  const findAiAssessedScore = useCallback(
    (responseId: string) => {
      if (
        roundState === 'post-between' &&
        (slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers') &&
        aiAssessedScores
      ) {
        const aiAssessedScore = aiAssessedScores.find(s => s.responseId === responseId);
        return aiAssessedScore;
      }
      return undefined;
    },
    [aiAssessedScores, roundState, slideType],
  );

  const currentAiAssessedScore = useMemo(() => {
    if (!currentStudentResponse) return undefined;
    return findAiAssessedScore(currentStudentResponse.id);
  }, [currentStudentResponse, findAiAssessedScore]);

  const findParticipantIsFinishedStatus = (participantId: string) => {
    const response = findParticipantResponse(participantId);
    if (!response) return 'notStarted';
    return response.isFinished ? 'done' : 'writing';
  };

  const isMobileView = useMemo(() => {
    return !isLargerThan800 || (!isLargerThan900 && currentAiAssessedScore)
      ? true
      : false;
  }, [isLargerThan800, isLargerThan900, currentAiAssessedScore]);

  //usememo to return all responses in alphabetical order by name or nickname if name is not available
  const sortedResponses = useMemo(() => {
    return responses.sort((a, b) => {
      const aParticipant = participantsById[a.uid];
      const bParticipant = participantsById[b.uid];
      if (aParticipant.name && bParticipant.name) {
        return aParticipant.name.localeCompare(bParticipant.name);
      } else {
        return aParticipant.nickName.localeCompare(bParticipant.nickName);
      }
    });
  }, [responses, participantsById]);

  useEffect(() => {
    if (
      roundState === 'post-between' &&
      (slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers') &&
      !!!aiAssessedScores
    ) {
      setShowGenerateFeedbackOverlay(true);
    } else {
      setShowGenerateFeedbackOverlay(false);
    }
  }, [aiAssessedScores, roundState, slideType]);

  return (
    <>
      {showGenerateFeedbackOverlay && (
        <ActivationOverlay>
          <ActivateCurimoteContainer
            style={{ padding: 20, background: theme.colors.white }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              {!!error && (
                <IconWrapper
                  height="8px"
                  width="8px"
                  style={{
                    position: 'absolute',
                    top: -12,
                    right: -12,
                    border: `1px solid ${theme.colors.iconGrey}`,
                    borderRadius: '50%',
                    padding: 2,
                  }}
                  onClick={() => setShowGenerateFeedbackOverlay(false)}
                >
                  <CrossSvg />
                </IconWrapper>
              )}
              <Button
                icon={<MagicWandSvg />}
                styles={
                  isFetchingAiAssessedScores ? { background: theme.colors.iconGrey } : {}
                }
                onClick={() => {
                  if (isFetchingAiAssessedScores) return;
                  onFetchAllAiScores();
                }}
                loading={isFetchingAiAssessedScores}
                disabled={isFetchingAiAssessedScores}
              >
                {isFetchingAiAssessedScores ? (
                  <T keyName="Generating feedback" defaultValue="Generating feedback" />
                ) : (
                  <T keyName="Generate feedback" defaultValue="Generate feedback" />
                )}
              </Button>
              {!!error && (
                <div
                  style={{
                    margin: '10px auto 0px auto',
                  }}
                >
                  <Alert
                    message={
                      <T
                        keyName="We're sorry! 😔 There was an error generating feedback, please try again"
                        defaultValue="We're sorry! 😔 There was an error generating feedback, please try again."
                      />
                    }
                    type="error"
                  />
                </div>
              )}

              {(useOpenAIIsDown || !!isOpenAiDown) && (
                <div
                  style={{
                    margin: '10px auto',
                  }}
                >
                  <Alert
                    message={
                      <T
                        keyName="We're sorry! 😔 Our AI system is currently having issues This means we will be unable to generate feedback at this time"
                        defaultValue="We're sorry! 😔 Our AI system is currently having issues. This means we will be unable to generate feedback at this time."
                      />
                    }
                    type="error"
                  />
                </div>
              )}
            </div>
          </ActivateCurimoteContainer>
        </ActivationOverlay>
      )}
      <StreamingResponsesLayout
        style={{
          gridTemplateColumns: isMobileView
            ? '1fr'
            : isLargerThan1000
            ? 'minmax(300px, 350px) 1.618fr'
            : isLargerThan800
            ? 'minmax(280px, 300px) 1.618fr'
            : 'minmax(300px, 350px) 1.618fr',
        }}
      >
        {!isMobileView && (
          <ParticipantsList
            participants={sortedParticipants}
            currentSelectedStudentId={currentSelectedStudentId}
            setCurrentSelectedStudentId={setCurrentSelectedStudentId}
            findParticipantResponse={findParticipantResponse}
            responseCharacterLimit={responseCharacterLimit}
            slideType={slideType}
            findParticipantIsFinishedStatus={findParticipantIsFinishedStatus}
          />
        )}
        {isMobileView && (
          <List>
            {sortedResponses.map(r => (
              <ListItem
                key={r.id}
                response={r}
                participantsById={participantsById}
                onDismiss={onDismiss}
                requireRealNames={requireRealNames}
                slideType={slideType}
                participantIsFinishedStatus={findParticipantIsFinishedStatus(r.uid)}
                aiAssessedScore={findAiAssessedScore(r.id)}
                isFetchingAiAssessedScores={isFetchingAiAssessedScores}
                gameId={gameId}
                slideId={slideId}
              />
            ))}
          </List>
        )}
        {!isMobileView && (
          <List
            style={{
              height: currentAiAssessedScore ? '100%' : 'fit-content',
            }}
          >
            {currentStudentResponse && (
              <ListItem
                key={currentStudentResponse.id}
                response={currentStudentResponse}
                participantsById={participantsById}
                onDismiss={onDismiss}
                requireRealNames={requireRealNames}
                slideType={slideType}
                participantIsFinishedStatus={findParticipantIsFinishedStatus(
                  currentStudentResponse.uid,
                )}
                aiAssessedScore={currentAiAssessedScore}
                isFetchingAiAssessedScores={isFetchingAiAssessedScores}
                gameId={gameId}
                slideId={slideId}
              />
            )}
            {currentSelectedStudentId &&
              findParticipantIsFinishedStatus(currentSelectedStudentId) ===
                'notStarted' && (
                <NotStartedResponseEmptyState>
                  {participantsById[currentSelectedStudentId].name ||
                    participantsById[currentSelectedStudentId].nickName}{' '}
                  - <T keyName="Not yet started" defaultValue="Not yet started" />
                </NotStartedResponseEmptyState>
              )}
          </List>
        )}
      </StreamingResponsesLayout>
    </>
  );
};
const List = styled.ul`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 2px 0px;
  grid-gap: 20px;
  justify-content: space-between;
  list-style: none;
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;

  &::before {
    position: absolute;
    content: attr(data-keyword);
  }
`;

const NotStartedResponseEmptyState = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  padding: 20px;
  width: 100%;
  font-size: 24px;
  font-weight: bold;
  color: ${theme.colors.iconGrey};
  border: 1px dashed ${theme.colors.iconGrey};
  border-radius: 10px;
  background-color: ${theme.colors.translucentWhite};
  margin-top: 2px;
`;

const ParticipantsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  width: 100%;
  min-width: 280px;
  overflow-y: auto;
  padding-right: 20px;
`;
const ParticipantButton = styled(Button)<{
  $status: 'writing' | 'done' | 'notStarted' | 'absent';
  $isSelected: boolean;
}>`
  display: flex;
  width: 100%;
  min-height: 80px;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  padding: 20px;
  margin-left: 10px;
  overflow: hidden;
  border: 2px solid
    ${({ $status }) =>
      $status === 'writing'
        ? theme.colors.blue
        : $status === 'done'
        ? theme.colors.green
        : $status === 'notStarted'
        ? theme.colors.grey
        : theme.colors.red};
  border-radius: 20px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-size: 20px;
  font-weight: bold;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  p {
    margin: 0;
  }
  p {
    font-size: 16px;
  }

  &:active,
  &:focus,
  &:hover {
    color: ${theme.colors.black};
    background-color: ${({ $status }) =>
      $status === 'writing'
        ? theme.colors.blueLight
        : $status === 'done'
        ? theme.colors.veryLightGreen
        : $status === 'notStarted'
        ? theme.colors.greyLight
        : theme.colors.veryLightRed};

    border: 2px solid
      ${({ $status }) =>
        $status === 'writing'
          ? theme.colors.blue
          : $status === 'done'
          ? theme.colors.green
          : $status === 'notStarted'
          ? theme.colors.grey
          : theme.colors.red};
  }

  ${({ $isSelected, $status }) =>
    $isSelected &&
    css`
      background-color: ${$status === 'writing'
        ? theme.colors.blueLight
        : $status === 'done'
        ? theme.colors.veryLightGreen
        : $status === 'notStarted'
        ? theme.colors.greyLight
        : theme.colors.veryLightRed};
    `}
`;

const ParticipantName = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  @media (max-width: 1000px) {
    font-size: 24px;
  }
`;
const StreamingResponsesLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 350px) 1.618fr;
  padding: 0px 20px 0px 10px;
  width: 100%;
  margin: 0 auto;
  height: calc(100vh - 200px);
  overflow: hidden;

  @media (max-width: 800px) {
    padding: 0px 20px;
  }
`;

export default StreamingLongAnswerList;
